import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  featuredNavigationHrefs,
  getNavigationItemByRoute,
  isRouteActive,
  navigationItems,
  primaryNavigationHrefs,
  supportingNavigationHrefs
} from "../../data/navigation/navigation";
import { t } from "../../lib/i18n";
import type { Locale } from "../../types/app";
import { Button } from "../common/Button";
import { Icon } from "../common/Icon";
import { NavIcon } from "./NavIcon";

interface MobileMenuProps {
  readonly menuId: string;
  readonly open: boolean;
  readonly route: string;
  readonly locale: Locale;
  readonly onClose: () => void;
  readonly onOpenSettings: () => void;
}

export function MobileMenu({ menuId, open, route, locale, onClose, onOpenSettings }: MobileMenuProps) {
  const [rendered, setRendered] = useState(open);
  const [closing, setClosing] = useState(false);
  const isArabic = locale === "ar";
  const menuRef = useRef<HTMLElement>(null);
  const primaryItems = navigationItems.filter((item) =>
    primaryNavigationHrefs.includes(item.href as (typeof primaryNavigationHrefs)[number])
  );
  const featuredItems = navigationItems.filter((item) =>
    featuredNavigationHrefs.includes(item.href as (typeof featuredNavigationHrefs)[number])
  );
  const supportingItems = navigationItems.filter((item) =>
    supportingNavigationHrefs.includes(item.href as (typeof supportingNavigationHrefs)[number])
  );
  const currentItem = getNavigationItemByRoute(route);
  const currentLabel = currentItem ? (isArabic ? currentItem.labelAr : currentItem.labelEn) : (isArabic ? "أذكاري" : "Athkari");

  useEffect(() => {
    if (open) {
      setRendered(true);
      setClosing(false);
      return undefined;
    }

    if (!rendered) return undefined;

    setClosing(true);
    const timer = window.setTimeout(() => {
      setRendered(false);
      setClosing(false);
    }, 220);

    return () => window.clearTimeout(timer);
  }, [open, rendered]);

  useEffect(() => {
    if (!rendered) return undefined;

    const previousOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    if (open) {
      requestAnimationFrame(() => menuRef.current?.focus());
    }

    return () => {
      document.body.style.overflow = previousOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose, open, rendered]);

  if (!rendered) return null;

  const menu = (
    <div
      className={`modal-backdrop mobile-menu-backdrop ${closing ? "is-closing" : "is-open"}`}
      data-direction={isArabic ? "rtl" : "ltr"}
      role="presentation"
      onPointerDown={onClose}
    >
      <aside
        ref={menuRef}
        id={menuId}
        className={`mobile-menu mobile-menu--sheet ${closing ? "is-closing" : "is-open"}`}
        role="dialog"
        aria-modal="true"
        aria-label={isArabic ? "قائمة التنقل" : "Navigation menu"}
        dir={isArabic ? "rtl" : "ltr"}
        lang={locale}
        tabIndex={-1}
        onPointerDown={(event) => event.stopPropagation()}
      >
        <header className="mobile-menu__header">
          <div className="mobile-menu__hero">
            <div className="mobile-menu__hero-bar">
              <span className="mobile-menu__eyebrow">
                <Icon name="sparkles" />
                <span>{isArabic ? "تنقل سريع" : "Quick navigation"}</span>
              </span>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="mobile-menu__close"
                onClick={onClose}
                aria-label={isArabic ? "إغلاق القائمة" : "Close menu"}
              >
                <Icon name="close" />
              </Button>
            </div>

            <div className="mobile-menu__brand-card">
              <div className="mobile-menu__brand">
                <span className="brand__logo-shell" aria-hidden="true">
                  <img className="brand__wordmark" src="/assets/icons/athkari-wordmark.svg" alt="" />
                </span>
                <div className="mobile-menu__headline">
                  <strong>{t(locale, "appName")}</strong>
                  <small>{t(locale, "appTagline")}</small>
                </div>
              </div>

              <div className="mobile-menu__current-panel">
                <span className="mobile-menu__current-label">{isArabic ? "الصفحة الحالية" : "Current page"}</span>
                <span className="mobile-menu__current">{currentLabel}</span>
              </div>
            </div>
          </div>
        </header>

        <div className="mobile-menu__body">
          <section className="mobile-menu__section mobile-menu__section--primary">
            <div className="mobile-menu__section-head">
              <p>{isArabic ? "الأساسيات" : "Essentials"}</p>
              <span className="mobile-menu__section-count">{primaryItems.length}</span>
            </div>
            <nav className="mobile-menu__primary-list" aria-label={isArabic ? "الروابط الأساسية" : "Essential links"}>
              {primaryItems.map((item) => {
                const active = isRouteActive(route, item.href);
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    className={`mobile-menu__primary-link ${active ? "active" : ""}`}
                    onClick={onClose}
                    aria-current={active ? "page" : undefined}
                  >
                    <span className="mobile-menu__link-main">
                      <NavIcon name={item.icon} />
                      <span className="mobile-menu__link-label">{locale === "ar" ? item.labelAr : item.labelEn}</span>
                    </span>
                    <Icon name="arrow-up-right" className="icon mobile-menu__link-arrow" />
                  </a>
                );
              })}
            </nav>
          </section>

          <section className="mobile-menu__section mobile-menu__section--feature">
            <div className="mobile-menu__section-head">
              <p>{isArabic ? "أدواتك" : "Your tools"}</p>
              <span className="mobile-menu__section-count">{featuredItems.length}</span>
            </div>
            <nav className="mobile-menu__feature-grid" aria-label={isArabic ? "الأدوات السريعة" : "Quick tools"}>
              {featuredItems.map((item) => {
                const active = isRouteActive(route, item.href);
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    className={`mobile-menu__feature-link ${active ? "active" : ""}`}
                    onClick={onClose}
                    aria-current={active ? "page" : undefined}
                  >
                    <span className="mobile-menu__feature-top">
                      <NavIcon name={item.icon} />
                      <Icon name="arrow-up-right" className="icon mobile-menu__feature-arrow" />
                    </span>
                    <span className="mobile-menu__feature-label">{locale === "ar" ? item.labelAr : item.labelEn}</span>
                  </a>
                );
              })}
            </nav>
          </section>

          <section className="mobile-menu__section mobile-menu__section--supporting">
            <div className="mobile-menu__section-head">
              <p>{isArabic ? "المزيد" : "More"}</p>
              <span className="mobile-menu__section-count">{supportingItems.length}</span>
            </div>
            <nav className="mobile-menu__support-list" aria-label={isArabic ? "صفحات إضافية" : "Additional pages"}>
              {supportingItems.map((item) => {
                const active = isRouteActive(route, item.href);
                return (
                  <a
                    key={item.href}
                    href={item.href}
                    className={`mobile-menu__support-link ${active ? "active" : ""}`}
                    onClick={onClose}
                    aria-current={active ? "page" : undefined}
                  >
                    <span className="mobile-menu__link-main">
                      <NavIcon name={item.icon} />
                      <span className="mobile-menu__link-label">{locale === "ar" ? item.labelAr : item.labelEn}</span>
                    </span>
                    <Icon name="arrow-up-right" className="icon mobile-menu__link-arrow" />
                  </a>
                );
              })}
            </nav>
          </section>
        </div>

        <footer className="mobile-menu__footer">
          <button
            type="button"
            className="mobile-menu__settings"
            onClick={() => {
              onClose();
              onOpenSettings();
            }}
          >
            <NavIcon name="settings" />
            <span>{t(locale, "settings")}</span>
          </button>
        </footer>
      </aside>
    </div>
  );

  if (typeof document === "undefined") return menu;

  return createPortal(menu, document.body);
}
